import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import CommonSelect from "../../../../core/common/commonSelect";
import { useFeeConfig } from "../hooks/useFeeConfig";
import { useCourse } from "../../../configuration/course/hooks/useCourse";
import { useFeeType } from "../../../feetypes/hooks/useFeeType";
import { showToast } from "../../../../core/utils/alert";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import type { ObjectSchema } from "yup";
import {
  FeeConfigPayloadType,
  StageFeesEntity as ReqStageFeesEntity,
} from "../types/feeConfigRequest";
import { StageFeesEntity as ResStageFeesEntity } from "../types/feeConfigResponse";

interface FeeConfigFormData {
  course_id: string;
  type_id: string;
  status: string;
}

interface CourseStageData {
  id?: number;
  stage_id: number;
  name: string;
  amount: string | number;
}

const AddFeeConfig = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const {
    addFeeConfig,
    updateFeeConfig,
    useGetFeeConfigById,
    isAdding,
    isUpdating,
  } = useFeeConfig();
  const { id } = useParams<{ id: string }>();
  const [isEdit, setIsEdit] = useState<boolean>(!!id);
  const { feeConfig, loading: isLoadingFeeConfig } = useGetFeeConfigById(
    id || ""
  );

  const [courseStages, setCourseStages] = useState<CourseStageData[]>([]);

  const { getCourseState } = useCourse();
  const { courses, isLoading: isLoadingCourses } = getCourseState();

  const { getFeeTypeState } = useFeeType();
  const { feetypes, isLoading: isLoadingFeeTypes } = getFeeTypeState();

  // Explicitly type the schema to match FeeConfigFormData
  const validationSchema: ObjectSchema<FeeConfigFormData> = yup.object({
    course_id: yup.string().required("Course is required"),
    type_id: yup.string().required("Fee type is required"),
    status: yup.string().required("Status is required"),
  });

  const filteredFeeTypes = useMemo(
    () => feetypes.filter((feeType) => feeType.type === "coursefee"),
    [feetypes]
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<FeeConfigFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: useMemo(
      () => ({
        course_id: "",
        type_id:
          filteredFeeTypes.length > 0 ? String(filteredFeeTypes[0].id) : "",
        status: "active",
      }),
      [filteredFeeTypes]
    ),
  });

  const watchedCourseId = watch("course_id");
  useEffect(() => {
    if (!isEdit) {
      const selectedCourse = courses.find(
        (course) => course.id === Number(watchedCourseId)
      );
      if (selectedCourse) {
        const newStages = selectedCourse?.courseStages?.map(
          (stage: { id: number; name: string }) => ({
            id: stage.id, // courseStage ID
            name: stage.name,
            stage_id: stage.id, // Explicitly assign CourseStage ID to stage_id
            amount: 0, // Initial amount
          })
        );
        setCourseStages(newStages);
      } else {
        setCourseStages([]);
      }
    }
  }, [watchedCourseId, courses, isEdit]);

  // Effect to set form values and stages when editing
  useEffect(() => {
    setIsEdit(!!id);
    if (id && feeConfig?.coursefee) {
      const coursefeeData = feeConfig.coursefee;

      const courseId = coursefeeData.course?.id?.toString() || "";
      const typeId = coursefeeData.feeType?.id?.toString() || "";
      const statusValue = coursefeeData.status || "active";

      setValue("course_id", courseId, { shouldValidate: true });
      setValue("type_id", typeId, { shouldValidate: true });
      setValue("status", statusValue, { shouldValidate: true });

      const existingStages = (coursefeeData.stageFees || [])
        .filter(
          (sf): sf is ResStageFeesEntity =>
            sf?.stage_id != null && sf?.id != null
        )
        .map((stageFee: ResStageFeesEntity) => {
          const course = courses.find((c) => c.id === coursefeeData.course?.id);
          const stageDetail = course?.courseStages?.find(
            (cs) => cs.id === stageFee.stage_id
          );
          return {
            id: stageFee.id,
            stage_id: stageFee.stage_id,
            name: stageFee.stage_name,
            amount: stageFee.amount,
          };
        });
      setCourseStages(existingStages);
    } else if (!id) {
      reset();
      setCourseStages([]);
    }
  }, [id, feeConfig, courses, feetypes, setValue, reset]);

  // Add this useEffect to update the fee type whenever filteredFeeTypes changes
  useEffect(() => {
    if (filteredFeeTypes.length > 0 && !watch("type_id")) {
      setValue("type_id", String(filteredFeeTypes[0].id), {
        shouldValidate: true,
      });
    }
  }, [filteredFeeTypes, setValue, watch]);

  const handleStageChange = (
    index: number,
    field: keyof CourseStageData,
    value: any
  ) => {
    const updatedStages = [...courseStages];
    updatedStages[index] = { ...updatedStages[index], [field]: value };
    setCourseStages(updatedStages);
  };

  const onSubmit: SubmitHandler<FeeConfigFormData> = async (data) => {
    const basePayload = {
      course_id: Number(data.course_id),
      type_id: Number(data.type_id),
      status: data.status,
    };

    const stageFeesPayload: ReqStageFeesEntity[] = courseStages.map(
      (stage) => ({
        ...(isEdit && stage.id ? { id: stage.id } : {}),
        stage_id: stage.stage_id,
        amount: Number(stage.amount) || 0,
      })
    );

    const payload: FeeConfigPayloadType = {
      ...basePayload,
      stageFees: stageFeesPayload,
    };

    try {
      let result;
      if (isEdit && id) {
        result = await updateFeeConfig(id, payload);
      } else {
        result = await addFeeConfig(payload);
      }

      if (result?.success) {
        showToast({
          title: "Success",
          text: isEdit
            ? "Course Fee updated successfully."
            : "Course Fee added successfully.",
          icon: "success",
          timer: 5000,
        });
        navigate(routes.feesConfiguration);
      } else {
        let errorMessage = isEdit
          ? "Failed to update course fee."
          : "Failed to add course fee.";

        const errorData = result?.error;

        if (typeof errorData === "string") {
          errorMessage = errorData;
        } else if (typeof errorData === "object" && errorData !== null) {
          if (
            "data" in errorData &&
            typeof (errorData as any).data === "object" &&
            (errorData as any).data !== null
          ) {
            const message = (errorData as { data: { message?: string } }).data
              .message;
            errorMessage = message || errorMessage;
          }
        }

        showToast({
          title: "Error",
          text: errorMessage,
          icon: "error",
          timer: 5000,
        });
      }
    } catch (submissionError: any) {
      console.error("Submission Error:", submissionError);
      showToast({
        title: "Error",
        text: "An unexpected error occurred during submission.",
        icon: "error",
        timer: 5000,
      });
    }
  };

  // Helper to find label for select options
  const findLabel = (
    options: { value: string; label: string }[],
    value: string
  ) => {
    return options.find((option) => option.value === value)?.label || "";
  };

  // Prepare options arrays once
  const courseOptions = useMemo(
    () => [
      { value: "", label: "Select a Course" },
      ...courses.map((course) => ({
        value: String(course.id),
        label: course.name,
      })),
    ],
    [courses]
  );

  const feeTypeOptions = useMemo(
    () => [
      { value: "", label: "Select a Type" },
      ...(filteredFeeTypes.length > 0
        ? [{ value: String(filteredFeeTypes[0].id), label: "Course Fee" }]
        : []),
    ],
    [filteredFeeTypes]
  );

  const isLoading =
    isLoadingFeeConfig ||
    isLoadingCourses ||
    isLoadingFeeTypes;
  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content content-two">
          {/* Updated Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">{isEdit ? "Edit" : "Add"} Course Fee</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.feesConfiguration}>Course Fee List</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEdit ? "Edit" : "Add"} Course Fee
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}
          <div className="row">
            <div className="col-md-12">
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* FeeConfig Core Information */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-calendar-event fs-16" />
                      </span>
                      <h4 className="text-dark">Course Fee Details</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-lg-6">
                        <div
                          className={`mb-3 ${errors.course_id ? "has-danger" : ""
                            }`}
                        >
                          <label className="form-label">
                            Course <span className="text-danger">*</span>
                          </label>
                          <Controller
                            name="course_id"
                            control={control}
                            render={({ field }) => (
                              <CommonSelect
                                className={`select ${errors.course_id ? "is-invalid" : ""
                                  }`}
                                options={courseOptions}
                                value={
                                  field.value
                                    ? {
                                      value: field.value,
                                      label: findLabel(
                                        courseOptions,
                                        field.value
                                      ),
                                    }
                                    : null
                                }
                                onChange={(selectedOption) => {
                                  field.onChange(selectedOption?.value || "");
                                }}
                                placeholder="Select a Course"
                                isDisabled={isLoadingCourses}
                              />
                            )}
                          />
                          {errors.course_id && (
                            <div className="invalid-feedback d-block">
                              {errors.course_id.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div
                          className={`mb-3 ${errors.type_id ? "has-danger" : ""
                            }`}
                        >
                          <label className="form-label">
                            Fee Type <span className="text-danger">*</span>
                          </label>
                          <Controller
                            name="type_id"
                            control={control}
                            render={({ field }) => (
                              <CommonSelect
                                className={`select ${errors.type_id ? "is-invalid" : ""
                                  }`}
                                options={feeTypeOptions}
                                value={
                                  filteredFeeTypes.length > 0
                                    ? {
                                      value:
                                        field.value ||
                                        String(filteredFeeTypes[0].id),
                                      label: "Course Fee",
                                    }
                                    : null
                                }
                                onChange={(selectedOption) => {
                                  field.onChange(selectedOption?.value || "");
                                }}
                                isDisabled={true}
                                placeholder="Select a Type"
                              />
                            )}
                          />
                          {errors.type_id && (
                            <div className="invalid-feedback d-block">
                              {errors.type_id.message}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* <div className="col-lg-6">
                        <div
                          className={`mb-3 ${
                            errors.status ? "has-danger" : ""
                          }`}
                        >
                          <label className="form-label">
                            Status <span className="text-danger">*</span>
                          </label>
                          <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                              <CommonSelect
                                className={`select ${
                                  errors.status ? "is-invalid" : ""
                                }`}
                                options={statusValueOptions}
                                value={
                                  field.value
                                    ? {
                                        value: field.value,
                                        label: findLabel(
                                          statusValueOptions,
                                          field.value
                                        ),
                                      }
                                    : null
                                }
                                onChange={(selectedOption) => {
                                  field.onChange(selectedOption?.value || "");
                                }}
                                placeholder="Select Status"
                              />
                            )}
                          />
                          {errors.status && (
                            <div className="invalid-feedback d-block">
                              {errors.status.message}
                            </div>
                          )}
                        </div>
                      </div> */}
                    </div>

                    {/* Display Course Stages based on selected course */}
                    {courseStages.length > 0 && (
                      <div className="card mt-0 border-top-0">
                        <div className="card-header bg-light border-top">
                          <h5 className="mb-0 text-dark">Course Level Fees</h5>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-bordered mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th style={{ width: "60%" }}>Level Name</th>
                                  <th>
                                    Fee Amount{" "}
                                    <span className="text-danger">*</span>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {courseStages.map((stage, index) => (
                                  <tr key={stage.stage_id}>
                                    <td>{stage.name}</td>
                                    <td>
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Enter fee amount"
                                        value={stage.amount}
                                        onChange={(e) =>
                                          handleStageChange(
                                            index,
                                            "amount",
                                            e.target.value
                                          )
                                        }
                                        min="0"
                                        step="any"
                                        required
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-end mt-4">
                  <Link
                    to={routes.feesConfiguration}
                    className="btn btn-light me-3"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading || isAdding || isUpdating}
                  >
                    {isLoading
                      ? "Loading..."
                      : isAdding
                        ? "Saving..."
                        : isUpdating
                          ? "Updating..."
                          : isEdit
                            ? "Update Course Fee"
                            : "Create Course Fee"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default AddFeeConfig;
